import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { OtpInputContainer } from './OtpInputContainer';
import FigoLottie from '../FigoLottie';
import styles from './OtpFlow.module.css';
import { useOtp } from '../../../hooks/useOtp';
import { RoundButton } from '../RoundButton';
import figoShield from '../../../assets/login/figo-shield-shine.svg';
import { FigoInputMessage } from '../FigoInputMessage';
import { INVALID_OTP } from '../../../utils/customErrors.utils';

const OtpFlow = ({
  onValidationSuccess = () => { },
  onVerificationTokenRetrieved = () => { },
}) => {
  const store = useSelector(({ quoting }) => quoting);
  const { t } = useTranslation('login');
  const location = useLocation();

  const { newCustomer, sessionInformation: { nopCommerceUser } } = store;
  const { email: emailFromStore, phoneNumber } = newCustomer;

  const query = new URLSearchParams(location.search);
  const email = emailFromStore || query.get('email') || '';

  const successTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(successTimerRef.current), []);

  const onTokenRetrieved = useCallback(() => {
    clearTimeout(successTimerRef.current);
    successTimerRef.current = setTimeout(onVerificationTokenRetrieved, 800);
  }, [onVerificationTokenRetrieved]);

  const {
    isResending,
    isSMSEnabled,
    otpCode,
    otpSuccess,
    resendOtpCode,
    resendTimer,
    setOtpCode,
    validateOtpCode,
    verificationState,
  } = useOtp(
    email,
    phoneNumber,
    onValidationSuccess,
    onTokenRetrieved,
  );

  const validateCode = useCallback((event) => {
    event?.preventDefault?.();
    if (!otpSuccess && !verificationState.isLoading) {
      validateOtpCode(otpCode);
    }
  }, [otpCode, otpSuccess, validateOtpCode, verificationState.isLoading]);

  const showMessages = useCallback(() => {
    if (verificationState.error) {
      let errorToShow = verificationState.error;
      if (verificationState.error.toLowerCase() === INVALID_OTP) {
        errorToShow = t('otpFlow.incorrectPasscode');
      }
      return (
        <FigoInputMessage message={errorToShow} />
      );
    }
    if (nopCommerceUser?.LoginMessage && !nopCommerceUser?.LoginSuccess) {
      return (
        <FigoInputMessage message={nopCommerceUser.LoginMessage} />
      );
    }
    if (otpSuccess) {
      return (
        <FigoInputMessage
          message={t('otpFlow.verificationSuccess', {
            channel: isSMSEnabled ? t('otpFlow.phone') : t('otpFlow.email'),
          })}
          type="success"
        />
      );
    }

    return null;
  }, [
    isSMSEnabled,
    otpSuccess,
    t,
    verificationState.error,
    nopCommerceUser,
  ]);

  return (
    <form className={styles.container} onSubmit={validateCode}>
      <div className={styles.content}>
        <img alt="" src={figoShield} />

        <h1 className={styles.title}>
          {t(isSMSEnabled ? 'otpFlow.checkPhone' : 'otpFlow.checkInbox')}
        </h1>

        <p className={styles.description}>
          {t('otpFlow.codeSent', {
            channelValue: isSMSEnabled ? phoneNumber : email,
          })}
        </p>
      </div>

      <div className={styles.otpInputContainer}>
        <OtpInputContainer
          length={6}
          onComplete={validateCode}
          otpCode={otpCode}
          otpError={verificationState.error}
          setOtpCode={setOtpCode}
        />

        {showMessages()}
      </div>

      <RoundButton
        containerClassName={styles.sendCodeButton}
        primary
        submit
      >
        {t('verify')}
      </RoundButton>

      <div className={styles.resendContainer}>
        {resendTimer > 0 ? (
          <span className={styles.resendText}>
            {t('otpFlow.resendTimer', { seconds: resendTimer })}
          </span>
        ) : (
          <button
            className={styles.resendButton}
            disabled={isResending}
            onClick={resendOtpCode}
            type="button"
          >
            {isResending ? (
              <>
                <FigoLottie height={16} width={16} />

                <span className={styles.sendingText}>
                  {t('otpFlow.sending')}
                </span>
              </>
            ) : (
              t('otpFlow.resendCode')
            )}
          </button>
        )}
      </div>
    </form>
  );
};

export { OtpFlow };
